/// <amd-module name="Build/Stats/PipelinesMetricsContainer" />
import * as React from "react";

import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";

import { PieChart } from "TFS/Views/PieChart/PieChart";

export interface PipelinesMetricsPayload {
    passed: number;
    failed: number;
}

interface PipelinesMetricsContainerProps extends IVssComponentProperties {
    numOfDays: number;
    succeededText: string;
    fetchMetrics: (numOfDays: number) => Promise<PipelinesMetricsPayload | null>;
}

interface PipelinesMetricsContainerState {
    metrics: PipelinesMetricsPayload | undefined;
}

export class PipelinesMetricsContainer extends VssComponent<PipelinesMetricsContainerProps, PipelinesMetricsContainerState> {
    protected _colors: string[];

    constructor(props: PipelinesMetricsContainerProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            metrics: undefined
        };

        const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
        const successColor = `rgba(${themeService.getThemeValue("palette-accent2-dark")}, 1)`;
        const failedColor = `rgba(${themeService.getThemeValue("palette-neutral-4")}, 1)`;
        this._colors = [failedColor, successColor];
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._updateMetrics();
    }

    public componentDidUpdate(prevProps: PipelinesMetricsContainerProps, prevState: PipelinesMetricsContainerState): void {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.numOfDays !== this.props.numOfDays) {
            this._updateMetrics();
        }
    }

    public render(): JSX.Element | null {
        return (
            <div className="pipeline-metrics-wrapper">
                {this.state.metrics ? (
                    <PipelinesMetricsTile
                        passed={this.state.metrics.passed}
                        failed={this.state.metrics.failed}
                        infoText={this.props.succeededText}
                        colors={this._colors}
                    />
                ) : null}
            </div>
        );
    }

    private _updateMetrics(): void {
        this.trackPromise(this.props.fetchMetrics(this.props.numOfDays)).promise.then(
            (data: PipelinesMetricsPayload | null) => {
                data &&
                    this.setState({
                        metrics: {
                            passed: data.passed,
                            failed: data.failed
                        }
                    });
            },
            () => {
                // no-op
            }
        );
    }
}

interface PipelinesMetricsTileProps {
    passed: number;
    failed: number;
    colors: string[];
    infoText: string;
}

const PipelinesMetricsTile = (props: PipelinesMetricsTileProps): JSX.Element => {
    const pieChartSize = 60; // this should be consistent with the value in scss file
    const outerRadius = pieChartSize / 2;

    const innerRadius = outerRadius - 2;

    const data: number[] = [props.passed ? props.failed : 100, props.passed];

    let passedPercentage = props.passed ? (props.passed / (props.passed + props.failed)) * 100 : 0;

    passedPercentage = Math.round(passedPercentage);
    const passedPercentageString = passedPercentage.toString().concat("%");

    return (
        <>
            <PieChart
                data={data}
                graphHeight={pieChartSize}
                graphWidth={pieChartSize}
                outerRadius={outerRadius}
                colors={props.colors}
                innerRadius={innerRadius}
                pieChartText={passedPercentageString}
                pieChartClass={"pie-chart"}
                textClass={"pie-chart-text flex-column justify-center"}
            />
            <div className={"fontSize succeeded-text word-break"}>{props.infoText}</div>
        </>
    );
}
